import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Heading,
  Input,
  Option,
  RadioInput,
  Select,
} from '@mm/components';
import PrimarySelect from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  hiddenVariables,
  normalizeVariableName,
} from '@mm/state/helpers/variable-names-helper';
import AttributeModel from '@mm/state/model/AttributeModel';
import {
  PromoterConditionRule,
  PromoterConditionRuleOperator,
  PromoterConditionRuleOperatorCaptions,
  PromoterConditions,
  PromoterModel,
  XRayCustomRule,
  XRayCustomRuleType,
} from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectAttributes } from '@mm/state/selectors/attributeSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useCustomerChatConditionsEditorPromoterState } from '@mm/stateful-components/CustomerChat/use-customer-chat-conditions-editor-state';
import { Dropdown, InputNumber, Radio } from 'antd';
import classNames from 'classnames';
import pluralize from 'pluralize';
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import validator from 'validator';
import styles from './CustomerChartConditionRulesEditor.module.css';
import './CustomerChatConditionRulesEditor.css';

export const itemsFromObject = (object: any) =>
  Object.keys(object).map<Option>(k => ({ id: k, label: object[k] }));

const XraySelectOptions: Option<
  NonNullable<PromoterModel['xrayMinActivityType']>
>[] = [
  { id: 'clicks', label: 'clicks' },
  { id: 'views', label: 'views' },
];

interface Props {
  usedInSlideout?: boolean;
  warnAboutMultipleRuleFreePromoters?: boolean;
  duplicateRules?: PromoterConditionRule[];
  localPromoter: ReturnType<
    typeof useCustomerChatConditionsEditorPromoterState
  >['localPromoter'];
  setLocalPromoter: ReturnType<
    typeof useCustomerChatConditionsEditorPromoterState
  >['setLocalPromoter'];
  updatePromoter: (updatedPromoter: PromoterModel) => void;
  updateLocalPromoterField: <K extends keyof PromoterModel>(
    key: K,
    value: PromoterModel[K],
  ) => void;
  onPromoterChange?: (
    key?: string,
    value?: any,
    onCatch?: () => void,
    partial?: Partial<PromoterModel>,
  ) => void;
  ruleTitle?: ReactNode;
  urlTitle?: ReactNode;
  canEditConditions?: boolean;
}

const RuleErrorTypes = {
  error: 1,
  duplicateRule: 2,
} as const;
type RuleError =
  | { type: typeof RuleErrorTypes.error; reason: string }
  | { type: typeof RuleErrorTypes.duplicateRule };

const DuplicateRuleWarning = () => (
  <Alert
    type="warning"
    showCloseIcon={false}
    className="customer-chat-conditions-editor__rule-duplicate-error"
  >
    <div>
      <div className="customer-chat-conditions-editor__rule-duplicate-error-heading">
        An existing triggers rule set conflicts with these rules, website
        visitors will be captured by that trigger.
      </div>
      <div className="customer-chat-conditions-editor__rule-duplicate-error-text">
        Please create a more specific conditions
      </div>
    </div>
  </Alert>
);

const RULES_TO_TITLE = {
  [XRayCustomRuleType.consumerData]: 'Consumer data on the user available',
  [XRayCustomRuleType.leadAttribute]: 'Lead has attribute',
};

const ruleToTitle = (name: string) => RULES_TO_TITLE[name] || name;
const HIDDEN_VARIABLES = [
  'created at',
  'created',
  'full name',
  'source',
  'source (old)',
  'address_line_1',
  'address_city',
  'address_state',
  'address_postal',
];

const hiddedVariable = (v: AttributeModel) => {
  const name = v.name.toLocaleLowerCase();
  return hiddenVariables.includes(name) || HIDDEN_VARIABLES.includes(name);
};

const buildVariable = (v: AttributeModel) => ({
  ...v,
  name: normalizeVariableName(v.name),
  key: `${v.system ? 's' : 'v'}-${v.id}`,
});

export const CustomerChatConditionRulesEditor: FunctionComponent<
  Props
> = props => {
  const {
    usedInSlideout,
    warnAboutMultipleRuleFreePromoters,
    duplicateRules = [],
    updatePromoter,
    updateLocalPromoterField,
    onPromoterChange,
    localPromoter,
    setLocalPromoter,
    ruleTitle = 'Show when website visitor meets:',
    urlTitle = 'Current URL',
    canEditConditions = true,
  } = props;
  const modalActions = useModalActions();

  const canAddCustomXRayRules = useSelector(
    combinedFeatureFlagsSelector,
  ).xray_custom_rules;

  const variables = useSelector(selectAttributes);

  const [advancedDelayValue, setAdvancedDelayValue] = useState(
    localPromoter?.xrayDelaySeconds ?? 30,
  );

  const [advancedMinActivityCount, setAdvancedMinActivityCount] = useState(
    localPromoter?.xrayMinActivityCount ?? 1,
  );

  const [friendlyVariables, setFriendlyVariables] = useState<AttributeModel[]>(
    [],
  );

  useEffect(() => {
    const items = variables
      .filter(v => !hiddedVariable(v))
      .map(v => buildVariable(v));
    setFriendlyVariables(items);
  }, [variables]);

  if (!localPromoter) {
    return null;
  }

  const addNewRule = () => {
    const oldRules = localPromoter.conditions.rules;
    const newOperator =
      oldRules.length === 0 ? 'is' : oldRules[oldRules.length - 1].operator;
    const updatedConditions: PromoterConditions = {
      ...localPromoter.conditions,
      rules: [...oldRules, { operator: newOperator, value: '' }],
    };
    const updatedPromoter = { ...localPromoter, conditions: updatedConditions };
    setLocalPromoter(updatedPromoter);
  };

  const removeRule = (index: number) => {
    const updatedConditions: PromoterConditions = {
      ...localPromoter.conditions,
      rules: [...localPromoter.conditions.rules],
    };
    updatedConditions.rules.splice(index, 1);
    const updatedPromoter = { ...localPromoter, conditions: updatedConditions };
    setLocalPromoter(updatedPromoter);
    updatePromoter(updatedPromoter);
  };

  const ruleError = (rule: PromoterConditionRule): RuleError | undefined => {
    switch (rule.operator) {
      case '*':
        break;
      case 'is':
      case 'is_not':
        if (!rule.value) {
          return {
            type: RuleErrorTypes.error,
            reason: 'This field cannot be empty',
          };
        }
        if (
          rule.value.search('://') > 0 ||
          !validator.isURL(rule.value, {
            require_protocol: false,
            protocols: ['http', 'https'],
          })
        ) {
          return {
            type: RuleErrorTypes.error,
            reason:
              'Enter valid URL without scheme (e.g., customers.ai/prices)',
          };
        }
        break;
      default:
        if (!rule.value) {
          return {
            type: RuleErrorTypes.error,
            reason: 'This field cannot be empty',
          };
        }
    }
    if (
      duplicateRules.some(
        r => r.value === rule.value && r.operator === rule.operator,
      )
    ) {
      return { type: RuleErrorTypes.duplicateRule };
    }
  };

  const rulesAndRuleErrors = localPromoter.conditions.rules.map(rule => ({
    rule,
    ruleError: ruleError(rule),
  }));

  const updateRule = <K extends keyof PromoterConditionRule>(
    index: number,
    key: K,
    value: PromoterConditionRule[K],
    persist: boolean = true,
  ) => {
    const updatedConditions: PromoterConditions = {
      ...localPromoter.conditions,
      rules: [...localPromoter.conditions.rules],
    };
    const updatedRule = {
      ...updatedConditions.rules[index],
      [key]: value,
    };
    updatedConditions.rules[index] = updatedRule;
    const updatedPromoter = { ...localPromoter, conditions: updatedConditions };
    setLocalPromoter(updatedPromoter);
    if (persist && !ruleError(updatedRule)) {
      updatePromoter(updatedPromoter);
    }
  };

  const onChangeOperator = (idx: number) => (k: any) => {
    if (!canEditConditions) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames[
            'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__CHANGE-OPERATOR'
          ],
        feature: 'customerChatEditConditions',
      });
      return;
    }

    updateRule(idx, 'operator', k as PromoterConditionRuleOperator);
  };

  const onAddNewCondition = () => {
    if (!canEditConditions) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames[
            'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__ADD-NEW-RULE'
          ],
        feature: 'customerChatEditConditions',
      });
      return;
    }
    addNewRule();
  };

  const onAdvancedReturningOnlyToggle = async () => {
    onPromoterChange?.(
      'xrayReturningVisitorOnly',
      !localPromoter?.xrayReturningVisitorOnly,
    );
  };

  const onAdvancedMinActivitySave = async (
    values: Pick<
      PromoterModel,
      'xrayMinActivityEnabled' | 'xrayMinActivityType' | 'xrayMinActivityCount'
    >,
  ) => {
    const valuesToApply = {
      xrayMinActivityEnabled: localPromoter?.xrayMinActivityEnabled,
      xrayMinActivityCount: localPromoter?.xrayMinActivityCount ?? 1,
      xrayMinActivityType: localPromoter?.xrayMinActivityType ?? 'clicks',
      ...values,
    };
    onPromoterChange?.(undefined, undefined, undefined, {
      id: localPromoter?.id,
      ...valuesToApply,
    });
  };

  const onAdvancedDelayValueChange = async (value: number) => {
    setAdvancedDelayValue(value);
  };

  const onAdvancedDelayBlur = async () => {
    onPromoterChange?.('xrayDelaySeconds', advancedDelayValue, () =>
      setAdvancedDelayValue(localPromoter?.xrayDelaySeconds || 0),
    );
  };

  const onAdvancedDelayCaptureToggle = async () => {
    onPromoterChange?.('xrayDelayEnabled', !localPromoter?.xrayDelayEnabled);
  };

  const onAdvancedMinActivityToggle = async () => {
    await onAdvancedMinActivitySave({
      xrayMinActivityEnabled: !localPromoter?.xrayMinActivityEnabled,
    });
  };

  const handleCanEditConditions = (name: string) => {
    if (canEditConditions) return true;
    modalActions.openUniversalUpgradeLandmineModal({
      landmineName: landmineNames[name],
      feature: 'customerChatEditConditions',
    });
    return false;
  };

  const onCustomRuleAdd = (item: XRayCustomRule) => {
    if (
      !handleCanEditConditions(
        'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__ADD-NEW-RULE',
      )
    ) {
      return;
    }
    if (!onPromoterChange) return;
    const settings = localPromoter?.settings || {};
    settings.xrayCustomRules ||= [];
    settings.xrayCustomRules.push(item);

    onPromoterChange('settings', settings);
  };

  const onConsumerDataAdd = () => {
    if (!canAddCustomXRayRule(XRayCustomRuleType.consumerData)) {
      return;
    }
    onCustomRuleAdd({ type: XRayCustomRuleType.consumerData, value: true });
  };

  const onLeadAttributeAdd = () => {
    if (!canAddCustomXRayRule(XRayCustomRuleType.leadAttribute)) {
      return;
    }
    onCustomRuleAdd({ type: XRayCustomRuleType.leadAttribute });
  };

  const onXrayCustomRulesModeChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (
      !handleCanEditConditions(
        'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__CHANGE-OPERATOR',
      )
    ) {
      return;
    }

    if (!onPromoterChange) return;
    const xrayCustomRulesMode = event.target.value as 'all' | 'any';
    onPromoterChange('settings', {
      ...localPromoter.settings,
      xrayCustomRulesMode,
    });
  };

  const onCustomRuleDelete = async (idx: number) => {
    if (
      !handleCanEditConditions(
        'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__CHANGE-OPERATOR',
      )
    ) {
      return;
    }
    if (!onPromoterChange) return;

    const settings = { xrayCustomRules: [], ...localPromoter.settings };
    settings.xrayCustomRules.splice(idx, 1);
    onPromoterChange('settings', settings);
  };

  const onVariableChange = (idx: number, option: AttributeModel) => {
    if (
      !handleCanEditConditions(
        'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__CHANGE-OPERATOR',
      )
    ) {
      return;
    }
    if (!onPromoterChange) return;

    const settings = { xrayCustomRules: [], ...localPromoter.settings };
    settings.xrayCustomRules = settings.xrayCustomRules.map((rule, i) =>
      i === idx
        ? {
            ...rule,
            variableId: option.id,
            variableType: option.system ? 'SystemVariable' : 'Variable',
          }
        : rule,
    );
    onPromoterChange('settings', settings);
  };

  const availableVariables = (rule: XRayCustomRule) => {
    const usedIds: number[] = [];
    (localPromoter?.settings?.xrayCustomRules || []).forEach(e => {
      if (e.variableId) usedIds.push(e.variableId);
    });

    return friendlyVariables.filter(
      v =>
        (!!rule.variableId && v.id === rule.variableId) ||
        !usedIds.includes(v.id),
    );
  };

  const findSelectedVariable = (rule: XRayCustomRule) => {
    if (!rule.variableId) return undefined;

    return friendlyVariables.find(
      v =>
        v.id === rule.variableId &&
        v.system === (rule.variableType === 'SystemVariable'),
    );
  };

  const canAddCustomXRayRule = (type: XRayCustomRuleType) => {
    if (!canAddCustomXRayRules) return false;

    if (type === XRayCustomRuleType.consumerData) {
      return !localPromoter?.settings?.xrayCustomRules?.some(
        e => e.type === XRayCustomRuleType.consumerData,
      );
    }
    if (type === XRayCustomRuleType.leadAttribute) {
      return !localPromoter?.settings?.xrayCustomRules?.some(
        e => e.type === XRayCustomRuleType.leadAttribute && !e.variableId,
      );
    }
    return true;
  };

  const onAddConditionSelect = (id: string) => {
    switch (id) {
      case 'url':
        return onAddNewCondition();
      case 'xrayReturningVisitorOnly':
        return onAdvancedReturningOnlyToggle();
      case 'xrayMinActivityEnabled':
        return onAdvancedMinActivityToggle();
      case 'xrayDelayEnabled':
        return onAdvancedDelayCaptureToggle();
      case XRayCustomRuleType.consumerData:
        return onConsumerDataAdd();
      case XRayCustomRuleType.leadAttribute:
        return onLeadAttributeAdd();
      default:
        return '';
    }
  };

  const showAlert =
    localPromoter?.conditions?.rules.filter(rule =>
      ['is', 'starts_with', 'ends_with'].includes(rule.operator),
    ).length >= 2;

  return (
    <>
      {warnAboutMultipleRuleFreePromoters && <DuplicateRuleWarning />}{' '}
      <div
        className={
          usedInSlideout
            ? 'customer-chat-conditions-editor__rule-mode--slideout'
            : 'customer-chat-conditions-editor__rule-mode'
        }
      >
        <span className="customer-chat-conditions-editor__rule-mode-caption">
          {ruleTitle}
        </span>
        <Radio
          className={styles.radioInput}
          name="ruleMode"
          checked={localPromoter.conditions.ruleMode === 'any'}
          value="any"
          onChange={() =>
            updateLocalPromoterField('conditions', {
              ...localPromoter.conditions,
              ruleMode: 'any',
            })
          }
        >
          At least one condition
        </Radio>
        <Radio
          className={styles.radioInput}
          name="ruleMode"
          checked={localPromoter.conditions.ruleMode === 'all'}
          value="all"
          onChange={() =>
            updateLocalPromoterField('conditions', {
              ...localPromoter.conditions,
              ruleMode: 'all',
            })
          }
        >
          Every condition
        </Radio>
        {localPromoter.conditions.ruleMode === 'all' &&
          localPromoter.conditions.rules.length > 1 &&
          showAlert && (
            <Alert
              className="customer-chat-conditions__alert-icon"
              type="error"
              showCloseIcon={false}
            >
              This combination of rules will result in the chat widget not
              appearing on any page
            </Alert>
          )}
      </div>
      <div className="customer-chat-conditions-editor__rules">
        {rulesAndRuleErrors.map(({ rule, ruleError }, idx) => (
          <div
            className={
              usedInSlideout
                ? 'customer-chat-conditions-editor__rule--slideout'
                : 'customer-chat-conditions-editor__rule'
            }
            key={idx}
          >
            <span className="customer-chat-conditions-editor__rule-variable">
              {urlTitle}
            </span>

            <Select
              hideClearButton
              hideCount
              singleSelect
              className={classNames(
                'customer-chat-conditions-editor__rule-operator',
                {
                  'customer-chat-conditions-editor__rule-operator--slideout':
                    usedInSlideout,
                },
              )}
              options={itemsFromObject(PromoterConditionRuleOperatorCaptions)}
              selected={[
                {
                  label: PromoterConditionRuleOperatorCaptions[rule.operator],
                  id: rule.operator,
                },
              ]}
              onChange={([opt]) => onChangeOperator(idx)(opt.id)}
            />
            {rule.operator !== '*' ? (
              <>
                {!usedInSlideout && (
                  <i className="fas fa-arrow-right customer-chat-conditions-editor__rule-arrow" />
                )}
                <div className="customer-chat-conditions-editor__rule-value-wrapper">
                  <Input
                    css={{
                      container: usedInSlideout
                        ? 'customer-chat-conditions-editor__rule-value--slideout'
                        : 'customer-chat-conditions-editor__rule-value',
                    }}
                    placeholder={
                      rule.operator === 'is' || rule.operator === 'is_not'
                        ? 'Enter URL without scheme (e.g., customers.ai/prices)'
                        : 'Enter URL'
                    }
                    value={rule.value}
                    onChange={e =>
                      updateRule(idx, 'value', e.target.value, false)
                    }
                    onBlur={() =>
                      ruleError?.type !== RuleErrorTypes.error &&
                      updatePromoter(localPromoter)
                    }
                    invalid={ruleError?.type === RuleErrorTypes.error}
                  />

                  {ruleError?.type === RuleErrorTypes.error && (
                    <Alert
                      type="error"
                      showCloseIcon={false}
                      css={{
                        container:
                          'customer-chat-conditions-editor__rule-value-error',
                      }}
                    >
                      {ruleError.reason}
                    </Alert>
                  )}

                  {ruleError?.type === RuleErrorTypes.duplicateRule && (
                    <DuplicateRuleWarning />
                  )}
                </div>
              </>
            ) : (
              !usedInSlideout && (
                <div className="customer-chat-conditions-editor__rule-spacer">
                  &nbsp;
                </div>
              )
            )}

            {localPromoter.conditions.rules.length > 1 && usedInSlideout && (
              <DeleteOutlined
                className="customer-chat-conditions-editor__rule-trash"
                onClick={() => removeRule(idx)}
              />
            )}

            {localPromoter.conditions.rules.length > 1 && !usedInSlideout && (
              <i
                className="far fa-trash-alt customer-chat-conditions-editor__rule-trash"
                onClick={() => removeRule(idx)}
              />
            )}
          </div>
        ))}
        {[
          'xrayMinActivityEnabled',
          'xrayReturningVisitorOnly',
          'xrayDelayEnabled',
        ].map(v =>
          localPromoter?.[v] ? (
            <div className={styles.conditionEntry} key={v}>
              <div className={styles.conditionName}>
                {(() => {
                  switch (v) {
                    case 'xrayMinActivityEnabled':
                      return 'Number of';
                    case 'xrayReturningVisitorOnly':
                      return (
                        <>
                          Returning visitors only{' '}
                          <span className={styles.subtleText}>
                            on second visit
                          </span>
                        </>
                      );
                    case 'xrayDelayEnabled':
                      return 'Time on site is greater than';
                    default:
                      return '';
                  }
                })()}
              </div>

              {v === 'xrayMinActivityEnabled' && (
                <>
                  <Select
                    hideClearButton
                    hideCount
                    singleSelect
                    className={styles.minActivityTypeSelect}
                    options={XraySelectOptions}
                    selected={[
                      XraySelectOptions.find(
                        v => v.id === localPromoter.xrayMinActivityType,
                      ) || XraySelectOptions[0],
                    ]}
                    onChange={v =>
                      onAdvancedMinActivitySave({
                        xrayMinActivityType: v[0].id,
                      })
                    }
                    disabled={!localPromoter.xrayMinActivityEnabled}
                  />
                  <InputNumber
                    className={styles.minActivityCountInput}
                    min={1}
                    onChange={v => v && setAdvancedMinActivityCount(v)}
                    onBlur={() =>
                      onAdvancedMinActivitySave({
                        xrayMinActivityCount: advancedMinActivityCount,
                      })
                    }
                    value={advancedMinActivityCount}
                  />
                  {pluralize(
                    localPromoter.xrayMinActivityType === 'views'
                      ? 'page'
                      : 'time',
                    advancedMinActivityCount,
                  )}
                </>
              )}

              {v === 'xrayDelayEnabled' && (
                <div
                  className={classNames(styles.timeInput, {
                    [styles.disabled]: !localPromoter?.xrayDelayEnabled,
                  })}
                >
                  <InputNumber
                    className={styles.numberInput}
                    min={1}
                    onChange={onAdvancedDelayValueChange}
                    onBlur={onAdvancedDelayBlur}
                    value={advancedDelayValue}
                    addonAfter="sec"
                  />
                </div>
              )}

              <span
                className={styles.deleteIcon}
                onClick={() => onAddConditionSelect(v)}
              >
                <DeleteOutlined />
              </span>
            </div>
          ) : null,
        )}
        {canAddCustomXRayRules && (
          <>
            <Heading size={5} isBold className={styles.boxHeading}>
              Custom Rules
            </Heading>
            <RadioInput
              className="customer-chat-conditions-editor__rule-mode-radio"
              name="xrayCustomRulesMode"
              checked={localPromoter?.settings?.xrayCustomRulesMode !== 'all'}
              value="any"
              label="At least one condition"
              onChange={onXrayCustomRulesModeChange}
            />
            <RadioInput
              name="xrayCustomRulesMode"
              checked={localPromoter?.settings?.xrayCustomRulesMode === 'all'}
              value="all"
              label="Every condition"
              onChange={onXrayCustomRulesModeChange}
            />
          </>
        )}

        {(localPromoter?.settings?.xrayCustomRules || []).map((e, idx) => (
          <div
            key={`${e.type}-${e.value}-${e.variableId}-${e.variableType}`}
            className={styles.conditionEntry}
          >
            <div className={styles.conditionName}>{ruleToTitle(e.type)}</div>
            {e.type === XRayCustomRuleType.leadAttribute && (
              <PrimarySelect
                className={styles.variableSelect}
                placeholder="Variable"
                onChange={(option: AttributeModel) => {
                  onVariableChange(idx, option);
                }}
                options={availableVariables(e)}
                value={findSelectedVariable(e)}
                showSearchFilter
              />
            )}
            <span
              className={styles.deleteIcon}
              onClick={() => onCustomRuleDelete(idx)}
            >
              <DeleteOutlined />
            </span>
          </div>
        ))}

        <div className="customer-chat-conditions-editor__dropdown-container">
          <Dropdown
            className="customer-chat-conditions-editor__dropdown"
            openClassName="customer-chat-conditions-editor__dropdown--open"
            overlayClassName="customer-chat-conditions-editor__dropdown-overlay"
            getPopupContainer={trigger => trigger.parentNode as HTMLElement}
            menu={{
              items: [
                { title: 'Page URL', id: 'url' },
                {
                  title: 'Number of clicks / views',
                  id: 'xrayMinActivityEnabled',
                  disabled:
                    localPromoter?.xrayMinActivityEnabled ||
                    !usedInSlideout ||
                    !onPromoterChange,
                },
                {
                  title: 'Returning visitors Only',
                  id: 'xrayReturningVisitorOnly',
                  disabled:
                    localPromoter?.xrayReturningVisitorOnly ||
                    !usedInSlideout ||
                    !onPromoterChange,
                },
                {
                  title: 'Time on site is greater than',
                  id: 'xrayDelayEnabled',
                  disabled:
                    localPromoter?.xrayDelayEnabled ||
                    !usedInSlideout ||
                    !onPromoterChange,
                },
                {
                  title: ruleToTitle(XRayCustomRuleType.consumerData),
                  id: XRayCustomRuleType.consumerData,
                  hidded: !canAddCustomXRayRules,
                  disabled:
                    !usedInSlideout ||
                    !onPromoterChange ||
                    !canAddCustomXRayRule(XRayCustomRuleType.consumerData),
                },
                {
                  title: ruleToTitle(XRayCustomRuleType.leadAttribute),
                  id: XRayCustomRuleType.leadAttribute,
                  hidded: !canAddCustomXRayRules,
                  disabled:
                    !usedInSlideout ||
                    !onPromoterChange ||
                    !canAddCustomXRayRule(XRayCustomRuleType.leadAttribute),
                },
              ].map(v =>
                v.hidded
                  ? null
                  : {
                      key: v.id,
                      disabled: v.disabled,
                      onClick: () => {
                        onAddConditionSelect(v.id);
                      },
                      label: (
                        <div
                          className={classNames(
                            'customer-chat-conditions-editor__dropdown-item',
                            'customer-chat-conditions-editor__dropdown-item--danger',
                          )}
                        >
                          {v.title}
                        </div>
                      ),
                    },
              ),
            }}
            trigger={['click']}
          >
            <div
              className={classNames(
                'customer-chat-conditions-editor__add-rule-button',
                {
                  'customer-chat-conditions-editor__add-rule-button--slideout':
                    usedInSlideout,
                },
              )}
            >
              <PlusOutlined style={{ fontSize: 16 }} /> New condition
            </div>
          </Dropdown>
        </div>
      </div>
    </>
  );
};
